import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { DoBoxRule } from '@this+that/models';
import log from 'loglevel';

import {
  UpdateDoBoxRuleInput,
  useCreateDoBoxRule,
  useDeleteDoBoxRule,
  useGetDoBoxRules,
  useUpdateDoBoxRule,
} from '../../services/doBoxRules.service';
import { useDoBoxRulesStore } from '../../store';
import { useCustomToast } from '../useCustomToast';

export interface UseDoBoxRulesReturn {
  rules: DoBoxRule[];
  activeCount: number;
  isLoading: boolean;
  createRule(instructions: string): Promise<void>;
  updateRule(input: UpdateDoBoxRuleInput): Promise<void>;
  deleteRule(id: string): Promise<void>;
  refetch(): Promise<void>;
}

export function useDoBoxRules(): UseDoBoxRulesReturn {
  const { t } = useTranslation();
  const { errorMessage } = useCustomToast();

  const rules = useDoBoxRulesStore((s) => s.rules);
  const isLoading = useDoBoxRulesStore((s) => s.isLoading);
  const hasLoaded = useDoBoxRulesStore((s) => s.hasLoaded);
  const { setRules, upsertRule, removeRule, setLoading, setHasLoaded } =
    useDoBoxRulesStore();

  const { getDoBoxRules } = useGetDoBoxRules();
  const { createDoBoxRule } = useCreateDoBoxRule();
  const { updateDoBoxRule } = useUpdateDoBoxRule();
  const { deleteDoBoxRule } = useDeleteDoBoxRule();

  useEffect(() => {
    if (hasLoaded) {
      return;
    }

    const ensureLoaded = async () => {
      setLoading(true);
      try {
        const fetched = await getDoBoxRules();
        setRules(fetched);
        setHasLoaded(true);
      } catch (err) {
        log.error('useDoBoxRules fetch failed:', err);
      } finally {
        setLoading(false);
      }
    };

    ensureLoaded();
  }, [hasLoaded, getDoBoxRules, setRules, setHasLoaded, setLoading]);

  const refetch = useCallback(async () => {
    try {
      const fetched = await getDoBoxRules();
      setRules(fetched);
    } catch (err) {
      log.error('useDoBoxRules refetch failed:', err);
    }
  }, [getDoBoxRules, setRules]);

  const createRule = useCallback(
    async (instructions: string) => {
      try {
        const created = await createDoBoxRule({ instructions });
        if (created) {
          upsertRule(created);
        }
      } catch (err) {
        errorMessage(t('doBox.rules.createError'));
        log.error('useDoBoxRules create failed:', err);
      }
    },
    [createDoBoxRule, upsertRule, errorMessage, t],
  );

  const updateRule = useCallback(
    async (input: UpdateDoBoxRuleInput) => {
      try {
        const updated = await updateDoBoxRule(input);
        if (updated) {
          upsertRule(updated);
        }
      } catch (err) {
        errorMessage(t('doBox.rules.updateError'));
        log.error('useDoBoxRules update failed:', err);
      }
    },
    [updateDoBoxRule, upsertRule, errorMessage, t],
  );

  const deleteRule = useCallback(
    async (id: string) => {
      const original = rules.find((r) => r.id === id);
      if (!original) {
        return;
      }

      removeRule(id);

      try {
        await deleteDoBoxRule(id);
      } catch (err) {
        upsertRule(original);
        errorMessage(t('doBox.settings.rules.deleteError'));
        log.error('useDoBoxRules delete failed:', err);
      }
    },
    [rules, removeRule, deleteDoBoxRule, upsertRule, errorMessage, t],
  );

  const activeCount = rules.filter((r) => r.active).length;

  return {
    rules,
    activeCount,
    isLoading,
    createRule,
    updateRule,
    deleteRule,
    refetch,
  };
}
